import React, { useCallback } from "react";
import { Helmet } from "react-helmet";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAirport } from "../../AirportContext";
import AirportSelectInput from "../../components/AirportSelectInput";
import PageLayout from "../../components/PageLayout";
import AircraftAnimation from "../../components/AircraftAnimation/AircraftAnimation";
import RadarSweep from "../../components/RadarSweep/RadarSweep";

export default function SelectAirportPage() {
  const [, setAirport] = useAirport();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const handleAirportLoaded = useCallback(
    (data) => {
      setAirport(data);
      history.push("/airport/" + data.icao);
    },
    [history, setAirport]
  );

  return (
    <PageLayout>
      <Helmet>
        <title>{t("seo.defaultTitle")}</title>
        <meta name="description" content={t("seo.defaultDescription")} />
      </Helmet>

      {/* Aircraft animation — behind content */}
      <AircraftAnimation opacity={0.09} />

      {/* Radar sweep — bottom right corner */}
      <RadarSweep size={280} />

      {/* Hero */}
      <div className="relative z-20 flex-1 flex items-center pb-12 justify-center px-5 py-16 pb-48">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(0,212,255,0.2) 0%, transparent 70%)",
                  transform: "scale(1.8)",
                }}
                aria-hidden="true"
              />
              <img
                src="/logo.png"
                alt={t("app.name") + " logo"}
                className="relative w-20 md:w-28 object-contain"
              />
            </div>
          </div>

          {/* Titles */}
          <div className="text-center mb-10">
            <p className="text-aviation-cyan text-xs font-bold tracking-[0.25em] uppercase mb-3">
              {t("app.tagline")}
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 text-glow-cyan">
              {t("app.name")}
            </h1>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-sm mx-auto">
              {t("app.subtitle")}
            </p>
          </div>

          {/* Search input */}
          <div className="glass-card rounded-card p-6 shadow-elevated mb-6">
            <AirportSelectInput
              initialError={location.state?.error}
              initialValue={location.state?.icao}
              onDataLoaded={handleAirportLoaded}
            />
          </div>

          {/* Uzbekistan airports badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-slate-600 mb-2">
            <span
              className="inline-block w-2 h-2 rounded-full bg-aviation-radarGreen animate-pulse-soft"
              aria-hidden="true"
            />
            <span>{t("status.online")} · UZTT · UZSS · UZSB · UZNU · UZNN · UZFF · UZFA · UZFN · UZST · UZSK · UZSA</span>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
